/*
 * Event tiles: places promotional tiles into the collection product grid.
 *
 * snippets/event-tiles.liquid renders each tile that applies to the current
 * collection into a hidden <template>. This script clones them into the
 * product list at each tile's configured position.
 *
 * The Smart Product Filter app replaces the theme's grid with its own list
 * (#gf-products) and redraws it on every sort, filter and page change, which
 * removes anything inserted into it. The app dispatches
 * `globoFilterRenderCompleted` on window after each redraw, so the tiles are
 * placed again then. Without the app, the theme's own #product-grid is used.
 *
 * Placement rules:
 * - A tile's position is "after this many products". Its size (1x1, 1x2, 2x1,
 *   2x2 or 3x1, columns x rows) sets the cells it covers. A tile that doesn't fit
 *   where it would start lets products go first until it does, so the grid has
 *   no gaps and product order never changes; a 1x1 tile always fits.
 * - On narrower grids 1x1 and 1x2 tiles stay one card wide; other sizes fill the row.
 * - A position past the last product puts the tile at the end.
 * - Tiles appear on the first page only.
 * - A tile is shown through its last day in the store's time zone. Liquid
 *   already skips expired tiles; checking again here covers cached pages.
 */
(function () {
  'use strict';

  var SIZES = { '1x1': [1, 1], '1x2': [1, 2], '2x1': [2, 1], '2x2': [2, 2], '3x1': [3, 1] };
  var DEFAULT_SIZE = '2x1';
  var SLOT_CLASS = 'event-tile-slot';

  function normalizePosition(position) {
    return Number.isFinite(position) && position > 0 ? Math.floor(position) : 0;
  }

  /** A tile's size ("1x1", "1x2", "2x1", "2x2" or "3x1") as columns and rows; anything else is 2x1. */
  function parseSize(value) {
    var size = SIZES[value] || SIZES[DEFAULT_SIZE];
    return { cols: size[0], rows: size[1] };
  }

  /**
   * The cells a tile covers on a grid with this many columns. On narrower
   * grids (phones) one-column tiles (1x1, 1x2) stay one card wide and every
   * other size fills the row, keeping its height in rows.
   */
  function tileSpan(size, columns) {
    columns = Math.max(1, columns);
    if (size.cols === 1) return { cols: 1, rows: size.rows };
    if (columns >= 3) return { cols: Math.min(size.cols, columns), rows: size.rows };
    return { cols: columns, rows: size.rows };
  }

  /**
   * Lays items out the way CSS grid auto-placement (sparse) does: each item
   * goes in the first position at or after the cursor where all its cells are
   * free. Returns the cell where the last item starts.
   */
  function autoPlace(items, columns) {
    var taken = {};
    var row = 0;
    var col = 0;
    var last = null;

    function fits(r, c, item) {
      if (c + item.cols > columns) return false;
      for (var y = r; y < r + item.rows; y++) {
        for (var x = c; x < c + item.cols; x++) {
          if (taken[y + ':' + x]) return false;
        }
      }
      return true;
    }

    items.forEach(function (item) {
      while (!fits(row, col, item)) {
        col += 1;
        if (col + item.cols > columns) {
          row += 1;
          col = 0;
        }
      }
      for (var y = row; y < row + item.rows; y++) {
        for (var x = col; x < col + item.cols; x++) taken[y + ':' + x] = true;
      }
      last = { row: row, col: col };
      col += item.cols;
    });

    return last;
  }

  /** Whether an item would start where the next product would, i.e. without leaving an empty cell. */
  function landsWithoutGap(items, item, columns) {
    var probe = autoPlace(items.concat([item]), columns);
    var single = autoPlace(items.concat([{ cols: 1, rows: 1 }]), columns);
    return probe.row === single.row && probe.col === single.col;
  }

  /**
   * Work out where each tile goes.
   * @param {Array<{id: string, position: number, lastDay: string, size: string}>} tiles
   * @param {number} productCount products currently in the list
   * @param {number} columns columns in the grid at the current width
   * @param {number} page current results page (1-based)
   * @param {string} today YYYY-MM-DD in the store's time zone
   * @returns {Array<{id: string, index: number, cols: number, rows: number}>}
   *   child index to insert each tile at, in insertion order (indexes count the
   *   tiles inserted before it), and the cells it spans
   */
  function planPlacements(tiles, productCount, columns, page, today) {
    if (page > 1) return [];
    columns = Math.max(1, columns);

    var live = tiles
      .map(function (tile, order) {
        return {
          id: tile.id,
          position: normalizePosition(tile.position),
          lastDay: tile.lastDay,
          span: tileSpan(parseSize(tile.size), columns),
          order: order,
        };
      })
      .filter(function (tile) {
        return !tile.lastDay || tile.lastDay >= today;
      })
      .sort(function (a, b) {
        return a.position - b.position || a.order - b.order;
      });

    var product = { cols: 1, rows: 1 };
    var placed = [];
    var productsBefore = 0;

    function itemsBefore(count) {
      // Products and already-placed tiles in document order, up to `count` products.
      var items = [];
      var next = 0;
      for (var p = 0; p <= count; p++) {
        while (next < placed.length && placed[next].productsBefore === p) {
          items.push(placed[next].span);
          next += 1;
        }
        if (p < count) items.push(product);
      }
      return items;
    }

    return live.map(function (tile, index) {
      productsBefore = Math.max(productsBefore, Math.min(tile.position, productCount));
      // A tile that would leave an empty cell before it lets products go first.
      while (productsBefore < productCount && !landsWithoutGap(itemsBefore(productsBefore), tile.span, columns)) {
        productsBefore += 1;
      }
      placed.push({ productsBefore: productsBefore, span: tile.span });
      return { id: tile.id, index: productsBefore + index, cols: tile.span.cols, rows: tile.span.rows };
    });
  }

  /** Today's date (YYYY-MM-DD) at a UTC offset written like Liquid's %z, e.g. "-0400". */
  function storeToday(now, utcOffset) {
    var match = /^([+-])(\d{2})(\d{2})$/.exec(utcOffset || '');
    var minutes = match ? (match[1] === '-' ? -1 : 1) * (Number(match[2]) * 60 + Number(match[3])) : 0;
    return new Date(now.getTime() + minutes * 60000).toISOString().slice(0, 10);
  }

  /**
   * The results page being shown: the page marked by the pagination when one
   * is given (the filter app can show a different page than the address bar
   * says, e.g. after filtering), otherwise the URL's `page` parameter.
   */
  function currentPage(search, paginationPage) {
    var page = parseInt(paginationPage, 10);
    if (!(page >= 1)) page = parseInt(new URLSearchParams(search).get('page'), 10);
    return page >= 1 ? page : 1;
  }

  // Viewport widths at which each card shape switches from 2 to 3 columns:
  // the theme's grid (<li class="grid__item">) at 990px, the filter app's
  // column classes (<div class="spf-col-lg-4 ...">) at 992px.
  var THREE_COLUMN_MIN_WIDTH = { theme: 990, app: 992 };

  /** Columns the product list shows for a card shape ("theme" or "app") at a viewport width. */
  function gridColumns(shape, viewportWidth) {
    return viewportWidth >= THREE_COLUMN_MIN_WIDTH[shape] ? 3 : 2;
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      planPlacements: planPlacements,
      parseSize: parseSize,
      tileSpan: tileSpan,
      gridColumns: gridColumns,
      storeToday: storeToday,
      currentPage: currentPage,
    };
  }

  if (typeof document === 'undefined') return;

  // While tiles are shown, the list is laid out as CSS grid (styles in
  // snippets/event-tiles.liquid) so a tile can span several columns and rows.
  // The grid copies the list's own gaps and column count, and cards keep
  // their own padding, so products look the same as in the app's layout.
  var GRID_CLASS = 'event-tiles-grid';
  var lastColumns = null;

  // The filter app's pagination marks the page it is showing with
  // aria-current (its wrapper's data-page attribute can be out of date).
  function paginationPage() {
    var current = document.querySelector('#gf_pagination_wrap [aria-current="page"]');
    return current ? current.textContent.trim() : null;
  }

  function findList() {
    return document.getElementById('gf-products') || document.getElementById('product-grid');
  }

  function cardShape(card) {
    return card.classList.contains('grid__item') ? 'theme' : 'app';
  }

  function currentColumns(list) {
    var card = list.querySelector(':scope > :not(.' + SLOT_CLASS + ')');
    return card ? gridColumns(cardShape(card), window.innerWidth) : null;
  }

  var GRID_PROPERTIES = ['--event-tiles-columns', '--event-tiles-track', '--event-tiles-column-gap', '--event-tiles-row-gap'];

  function leaveGridMode(list) {
    list.classList.remove(GRID_CLASS);
    GRID_PROPERTIES.forEach(function (name) {
      list.style.removeProperty(name);
    });
  }

  function gapValue(value) {
    return value === 'normal' ? '0px' : value;
  }

  function enterGridMode(list, sample, columns) {
    // Everything is measured with grid mode off, as the app or theme lays the
    // list out. Card widths don't always add up to the full row (the theme's
    // card width leaves room for a gutter the filter app removes), so the grid
    // keeps that leftover space at the end of each row, as the flex layout does.
    var listStyle = window.getComputedStyle(list);
    var contentWidth = list.clientWidth - parseFloat(listStyle.paddingLeft) - parseFloat(listStyle.paddingRight);
    var columnGap = parseFloat(gapValue(listStyle.columnGap)) || 0;
    var cardWidth = sample.getBoundingClientRect().width;
    var leftover = Math.max(0, contentWidth - columns * cardWidth - (columns - 1) * columnGap);

    list.style.setProperty('--event-tiles-columns', String(columns));
    list.style.setProperty('--event-tiles-track', 'calc((100% - ' + leftover.toFixed(2) + 'px - ' + (columns - 1) + ' * var(--event-tiles-column-gap)) / ' + columns + ')');
    list.style.setProperty('--event-tiles-column-gap', gapValue(listStyle.columnGap));
    list.style.setProperty('--event-tiles-row-gap', gapValue(listStyle.rowGap));
    list.classList.add(GRID_CLASS);
  }

  function makeSlot(sample, placement) {
    var slot = document.createElement(sample.tagName.toLowerCase());
    var sampleStyle = window.getComputedStyle(sample);
    slot.className = (cardShape(sample) === 'theme' ? 'grid__item ' : '') + SLOT_CLASS;
    slot.style.gridColumn = 'span ' + placement.cols;
    slot.style.gridRow = 'span ' + placement.rows;
    slot.style.paddingLeft = sampleStyle.paddingLeft;
    slot.style.paddingRight = sampleStyle.paddingRight;
    return slot;
  }

  function placeTiles() {
    var root = document.querySelector('.event-tiles');
    var list = findList();
    if (!root || !list) return;

    Array.prototype.forEach.call(list.querySelectorAll(':scope > .' + SLOT_CLASS), function (slot) {
      slot.remove();
    });
    leaveGridMode(list);

    var products = list.children;
    if (products.length === 0) return;

    var templates = {};
    var tiles = Array.prototype.map.call(root.querySelectorAll('template.event-tile-template'), function (template) {
      templates[template.dataset.tileId] = template;
      return {
        id: template.dataset.tileId,
        position: parseInt(template.dataset.position, 10),
        lastDay: template.dataset.lastDay,
        size: template.dataset.size,
      };
    });

    var sample = products[0];
    var columns = currentColumns(list);
    lastColumns = columns;

    var placements = planPlacements(
      tiles,
      products.length,
      columns,
      currentPage(window.location.search, paginationPage()),
      storeToday(new Date(), root.dataset.utcOffset)
    );
    if (placements.length === 0) return;

    enterGridMode(list, sample, columns);
    placements.forEach(function (placement) {
      var slot = makeSlot(sample, placement);
      slot.appendChild(templates[placement.id].content.cloneNode(true));
      list.insertBefore(slot, list.children[placement.index] || null);
    });
  }

  // Re-place when the column count changes. The list's own size is observed
  // (not window resize) because the grid only settles into its new size after
  // the window has finished resizing.
  var observedList = null;
  var resizeObserver =
    typeof ResizeObserver === 'undefined'
      ? null
      : new ResizeObserver(function () {
          var list = findList();
          if (list && currentColumns(list) !== lastColumns) placeTiles();
        });

  function observeList() {
    var list = findList();
    if (!resizeObserver || !list || list === observedList) return;
    if (observedList) resizeObserver.unobserve(observedList);
    resizeObserver.observe(list);
    observedList = list;
  }

  function update() {
    placeTiles();
    observeList();
  }

  // Crossing either card shape's column breakpoint also re-places the tiles.
  Object.keys(THREE_COLUMN_MIN_WIDTH).forEach(function (shape) {
    var query = window.matchMedia('(min-width: ' + THREE_COLUMN_MIN_WIDTH[shape] + 'px)');
    var onChange = function () {
      var list = findList();
      if (list && currentColumns(list) !== lastColumns) placeTiles();
    };
    if (query.addEventListener) query.addEventListener('change', onChange);
    else if (query.addListener) query.addListener(onChange);
  });

  window.addEventListener('globoFilterRenderCompleted', update);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', update);
  } else {
    update();
  }
})();
